<script setup lang="ts">
import type { NavItem } from '@nuxt/content'
import { type SearchItems, flattenNavigation } from '~/components/util'
import GlobalDiceRoller from '~/components/dice/GlobalDiceRoller.vue'
import type { Name, FamilyTree } from '~/components/familytree/familytree'
import NavigationMenu from '~/components/NavigationMenu.vue'
import type { Resource } from '~/assets/bindings'

const { page, navigation } = useContent()

const dm = useDM()

const showTopButton = ref(false)

const db = useDatabase()
const resourceQuery = computed(() => `select * from resources where page = '${page.value._id}'`)
const resources = useQuery<Resource, string>(db, 'resources', resourceQuery, (r) => r.name)
provide('resources', resources)

onMounted(() => {
  if (!page.value || !(page.value.public || dm.value)) {
    navigateTo('/')
  }
  window.addEventListener('scroll', () => {
    showTopButton.value = window.scrollY > 500
  })
})

const peopleData = computed<Record<Name, FamilyTree>>(() => {
  const pages = flattenNavigation(navigation.value)
  const allPeople = pages.filter((p) => p.people).map((page) => page.people)
  const result = Object.assign({}, ...allPeople)
  return result
})

const searchData = computed((): SearchItems => {
  const pages = flattenNavigation(navigation.value)
  const res: Record<string, NavItem> = {}

  pages
    .filter((page) => page.public || dm.value)
    .forEach((page) => {
      const tags = Array.isArray(page.searchTags) ? page.searchTags : Object.keys(page.searchTags ?? {})
      const allTags = new Set<string>([page.searchTitle, ...tags])
      allTags.forEach((tag) => {
        if (tag && res[tag]) console.warn('tag already exists:', tag)
        res[tag] = page
      })
    })
  return res
})

provide('search', searchData)
provide('people', peopleData)

const colorScheme = useCookie('colorScheme', { maxAge: 60 * 60 * 24 * 365, default: () => 'default' })
</script>

<template>
  <main
    key="main"
    :class="{ dm: dm, notdm: !dm, lightmode: colorScheme === 'light', darkmode: colorScheme === 'dark' }"
  >
    <NavigationMenu />
    <div id="pano" />
    <PageTitle />
    <div id="hero" />
    <slot />
    <TableOfContents />
    <GlobalDiceRoller />
    <GlobalPagePreview />
    <ClientOnly>
      <a v-if="showTopButton" href="#" class="floating-top-button noprint">
        <Icon name="fa6-solid:arrow-up" />
      </a>
    </ClientOnly>
  </main>
</template>

<style lang="postcss">
#hero {
  container: hero / inline-size;
  grid-area: hero;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  max-width: 100%;
}

#pano {
  container: pano / inline-size;
  grid-area: pano;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
}

.floating-top-button {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  font-size: 2em;
  border: 2px solid var(--primary);
  color: var(--text-highlight) !important;
  background: var(--background);
  width: 48px;
  height: 48px;
  text-align: center;
  border-radius: 50%;
}

@media (width > 950px) {
  .floating-top-button {
    display: none;
  }
}

main {
  padding: 0;
  container: main / inline-size;

  @media (min-width: 950px) {
    display: grid;
    gap: 0 1em;
    grid-template-areas:
      'menu menu menu menu'
      'pano pano pano pano'
      '. title title .'
      '. hero hero .'
      '. article toc .';
    grid-template-columns: 1fr minmax(25em, var(--text-width)) var(--sidebar-width) 1fr;
    grid-template-rows: 105px minmax(0.5rem, min-content) min-content min-content 1fr;
  }

  @media (max-width: 949px) {
    display: flex;
    padding-top: 58px; /* leaves room for the position: fixed navbar */
    justify-content: center;
    flex-direction: column;

    #hero {
      padding: 0 0.5rem;
    }
  }

  > header,
  > #hero,
  > article,
  > .table-of-contents {
    font-size: 18px;
  }

  > article {
    container: article / inline-size;
    grid-area: article;
    padding-bottom: 3rem;

    @media screen and (width < 950px) {
      flex: 1;
      max-width: 100%;
      padding: 0 0.5em 4rem;
    }
  }
}
</style>
